import { useSession } from "@/compat/next-auth-react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { User, Users } from "lucide-react";
import { useAdminScope } from "@/lib/hooks/use-admin-scope";
import { cn } from "@/lib/utils";

/**
 * Admin-only segmented control: "Just mine" | "All family".
 *
 * State persists via `useAdminScope` (localStorage `aim-admin-view-all`).
 * On change: persists the new value and invalidates every TanStack Query
 * cache (no filter) so all mounted pages refetch with the new scope —
 * pages read `viewAll` into their `queryKey` so this reliably triggers a
 * refetch without a full page reload.
 *
 * Hidden entirely for non-ADMIN users (and while the session is loading).
 */
export function AdminScopeToggle({
  className,
  compact = false,
}: {
  className?: string;
  /** Top-bar density: below xl the text labels hide (icon-only with title
   * tooltips) so the full navy bar fits at 1280/1440 without truncation.
   * The mobile drawer keeps labels (compact=false). */
  compact?: boolean;
}) {
  const { data: session, status } = useSession();
  const { viewAll, setViewAll } = useAdminScope();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();

  if (status !== "authenticated") return null;
  if (session?.user?.role !== "ADMIN") return null;

  function choose(next: boolean) {
    if (next === viewAll) return;
    setViewAll(next);
    queryClient.invalidateQueries();
  }

  return (
    <div
      role="radiogroup"
      aria-label="Data scope"
      className={cn(
        // Lives on the navy top bar / drawer chrome (navy in both themes) —
        // styled against the chrome vars, compact per the SDR bar density.
        "relative grid grid-cols-2 rounded-full border border-[var(--navy-700)] bg-[var(--navy-800)] p-0.5 text-[11px] font-semibold",
        className,
      )}
    >
      <motion.div
        aria-hidden
        className={cn(
          "absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full shadow-sm",
          viewAll ? "bg-amber-500" : "bg-white",
        )}
        animate={{ x: viewAll ? "100%" : "0%" }}
        transition={
          reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 32 }
        }
      />
      <button
        type="button"
        role="radio"
        aria-checked={!viewAll}
        onClick={() => choose(false)}
        title="Just mine"
        className={cn(
          "relative z-10 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 transition-colors active:scale-95",
          !viewAll
            ? "text-[var(--navy-900)]"
            : "text-[var(--navy-200)] hover:text-white",
        )}
      >
        <User className="h-3.5 w-3.5" />
        <span className={cn(compact && "hidden xl:inline")}>Just mine</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={viewAll}
        onClick={() => choose(true)}
        title="All family"
        className={cn(
          "relative z-10 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 transition-colors active:scale-95",
          viewAll
            ? "text-white"
            : "text-[var(--navy-200)] hover:text-white",
        )}
      >
        <Users className="h-3.5 w-3.5" />
        <span className={cn(compact && "hidden xl:inline")}>All family</span>
      </button>
    </div>
  );
}

/**
 * Amber banner shown when the admin scope toggle is ON — mirrors the rent
 * module's `AdminViewBanner`. Rendered at the top of the dashboard shell's
 * main content area (see `(dashboard)/layout.tsx`).
 */
export function AdminScopeBanner() {
  const { data: session, status } = useSession();
  const { viewAll, setViewAll } = useAdminScope();
  const queryClient = useQueryClient();

  if (status !== "authenticated") return null;
  if (session?.user?.role !== "ADMIN") return null;

  return (
    <AnimatePresence initial={false}>
      {viewAll && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-100 md:px-6">
            <span>
              Viewing data across <strong>all family members</strong> — admin mode
            </span>
            <button
              type="button"
              onClick={() => {
                setViewAll(false);
                queryClient.invalidateQueries();
              }}
              className="rounded px-2 py-0.5 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-800/40"
            >
              Switch to Just mine
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
